/**
 * ============================================================
 * KEJJA OS — src/pages/finance/CategoriesPage.tsx
 * Manage income/expense categories + their auto-match keywords.
 * ============================================================
 */

import { useState } from 'react';
import { Plus, Tag, Trash2, X } from 'lucide-react';

import { useFinanceContext } from './FinanceLayout';
import {
  useFinanceCategories, useCreateCategory, useDeleteCategory,
} from '@/hooks/useFinance';
import type { FinanceCategoryKind } from '@/lib/financeApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const SWATCH_COLORS = [
  '#059669', '#0891b2', '#db2777', '#10b981', '#f97316', '#eab308',
  '#06b6d4', '#8b5cf6', '#3b82f6', '#e11d48', '#dc2626', '#f43f5e',
  '#14b8a6', '#6b7280',
];

export default function CategoriesPage() {
  const { homeId } = useFinanceContext();
  const { data, isLoading } = useFinanceCategories(homeId);
  const categories = data?.categories ?? [];
  const [showAdd, setShowAdd] = useState(false);

  const income = categories.filter((c) => c.kind === 'income');
  const expense = categories.filter((c) => c.kind === 'expense');

  return (
    <div className="space-y-5 max-w-3xl">
      {/* ============ HEADER ============ */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">
            <Tag className="w-3.5 h-3.5" />
            Categories
          </div>
          <p className="text-sm text-muted-foreground">
            Keywords help KEJJA auto-categorize transactions from merchant names.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setShowAdd(!showAdd)}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          {showAdd ? <X className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
          {showAdd ? 'Close' : 'New category'}
        </Button>
      </div>

      {showAdd && <AddCategoryForm homeId={homeId} onDone={() => setShowAdd(false)} />}

      {/* ============ LISTS ============ */}
      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <>
          <CategorySection title="Expense" items={expense} homeId={homeId} />
          <CategorySection title="Income"  items={income}  homeId={homeId} />
        </>
      )}
    </div>
  );
}

/* ============ section ============ */

function CategorySection({
  title, items, homeId,
}: {
  title: string;
  items: Array<{ id: string; name: string; color: string | null; keywords: string[] }>;
  homeId: string;
}) {
  const del = useDeleteCategory(homeId);
  if (items.length === 0) return null;
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
        {title} · {items.length}
      </h3>
      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {items.map((c) => (
            <div key={c.id} className="px-4 py-3 flex items-center gap-3 group">
              <div
                className="h-8 w-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{
                  backgroundColor: c.color ? `${c.color}20` : 'var(--muted)',
                  color: c.color ?? 'var(--muted-foreground)',
                }}
              >
                {c.name[0]?.toUpperCase() ?? '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-foreground">{c.name}</div>
                {c.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {c.keywords.slice(0, 6).map((kw) => (
                      <Badge key={kw} variant="outline" className="text-[10px] font-normal">
                        {kw}
                      </Badge>
                    ))}
                    {c.keywords.length > 6 && (
                      <span className="text-[10px] text-muted-foreground">
                        +{c.keywords.length - 6}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  if (confirm(`Delete category "${c.name}"? Existing transactions will be kept but uncategorized.`)) {
                    del.mutate(c.id);
                  }
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-950/40 text-muted-foreground hover:text-rose-600"
                aria-label="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
}

/* ============ add form ============ */

function AddCategoryForm({ homeId, onDone }: { homeId: string; onDone: () => void }) {
  const create = useCreateCategory(homeId);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<FinanceCategoryKind>('expense');
  const [color, setColor] = useState(SWATCH_COLORS[0]!);
  const [kwRaw, setKwRaw] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) { setErr('Name required.'); return; }
    const keywords = kwRaw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    create.mutate(
      { name: name.trim(), kind, color, keywords },
      {
        onSuccess: () => { setName(''); setKwRaw(''); onDone(); },
        onError: (e: Error) => setErr(e.message ?? 'Could not save.'),
      },
    );
  };

  const inputCls = 'w-full h-9 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500';

  return (
    <Card className="border-orange-200 dark:border-orange-900/50 bg-gradient-to-br from-orange-50/40 to-transparent dark:from-orange-950/10">
      <CardContent className="p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setKind('expense')}
              className={cn(
                'flex-1 h-9 rounded-lg border text-sm font-medium transition-colors',
                kind === 'expense'
                  ? 'bg-orange-600 text-white border-orange-600'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted',
              )}
            >
              Expense
            </button>
            <button
              type="button"
              onClick={() => setKind('income')}
              className={cn(
                'flex-1 h-9 rounded-lg border text-sm font-medium transition-colors',
                kind === 'income'
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted',
              )}
            >
              Income
            </button>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === 'income' ? 'e.g. Freelance' : 'e.g. Subscriptions'}
              className={inputCls}
              autoFocus
              required
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              Color
            </label>
            <div className="flex flex-wrap gap-1.5">
              {SWATCH_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={cn(
                    'h-7 w-7 rounded-full border-2 transition-transform',
                    color === c ? 'border-foreground scale-110' : 'border-transparent hover:scale-105',
                  )}
                  style={{ backgroundColor: c }}
                  aria-label={`Pick ${c}`}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              Keywords (comma-separated)
            </label>
            <input
              type="text"
              value={kwRaw}
              onChange={(e) => setKwRaw(e.target.value)}
              placeholder="naivas, carrefour, quickmart"
              className={inputCls}
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              When a transaction's merchant name contains any of these, it'll auto-land here.
            </p>
          </div>

          {err && (
            <div className="text-xs text-rose-600 dark:text-rose-400 font-medium">{err}</div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              disabled={create.isPending}
              className="bg-orange-600 hover:bg-orange-700 text-white flex-1"
            >
              {create.isPending ? 'Saving…' : 'Create category'}
            </Button>
            <Button type="button" variant="outline" onClick={onDone}>Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
